/**
 * Types and utilities for resolving Excel chart cell references to data.
 * Shared between server (chart extraction) and client (live data resolution).
 */

import type { ChartType, ChartConfig } from './index.js'

// ─── Types ────────────────────────────────────────────────────────────

export interface ExcelChartSeries {
  name: string
  catRef?: string   // e.g. "Sheet1!$A$2:$A$10"
  valRef?: string   // e.g. "Sheet1!$B$2:$B$10"
  color?: string    // hex without # (e.g. "4F81BD")
  /** For combo charts: which chart type this series renders as */
  seriesChartType?: 'bar' | 'line' | 'area'
  /** '0' = primary (left), '1' = secondary (right) Y axis */
  yAxisId?: string
  /** Bubble size reference for bubble charts */
  bubbleSizeRef?: string
}

export interface ChartAnchor {
  from: { row: number; col: number; rowOff: number; colOff: number }
  to: { row: number; col: number; rowOff: number; colOff: number }
}

/** Stored in Y.Doc charts Y.Map */
export interface YDocChartEntry {
  type: 'chart'
  chartId: string
  chartType: ChartType
  title?: string
  series: ExcelChartSeries[]
  stacked?: boolean
  anchor: ChartAnchor
  rawXml: string
  rawRels?: string
  /** Files referenced by chart rels (e.g., style/color XMLs, images) — key: zip path, value: base64 content */
  relatedFiles?: Record<string, string>
  chartFileName: string
  xAxisLabel?: string
  yAxisLabel?: string
  /** Chart frame background color (hex without #) */
  backgroundColor?: string
  /** Chart frame border color (hex without #) */
  borderColor?: string
  /** Axis number format code (e.g. "0.0%", "#,##0") */
  valAxisNumFmt?: string
  /** Whether data labels are shown */
  showDataLabels?: boolean
}

// ─── Cell Reference Parsing ──────────────────────────────────────────

/** Parse an Excel cell reference like "$B$2" into { col, row } (0-based) */
export function parseCellRef(ref: string): { col: number; row: number; absCol: boolean; absRow: boolean } | null {
  const m = ref.match(/^(\$?)([A-Z]+)(\$?)(\d+)$/)
  if (!m) return null
  let col = 0
  for (const ch of m[2]) col = col * 26 + (ch.charCodeAt(0) - 64)
  col -= 1
  return { col, row: parseInt(m[4], 10) - 1, absCol: m[1] === '$', absRow: m[3] === '$' }
}

/** Parse "Sheet1!$A$2:$B$10" into sheet + start/end cell refs */
export function parseRangeRef(ref: string): {
  sheet?: string
  start: NonNullable<ReturnType<typeof parseCellRef>>
  end: NonNullable<ReturnType<typeof parseCellRef>>
} | null {
  let sheet: string | undefined
  let range = ref
  const bangIdx = ref.indexOf('!')
  if (bangIdx >= 0) {
    sheet = ref.substring(0, bangIdx).replace(/^'|'$/g, '')
    range = ref.substring(bangIdx + 1)
  }
  const parts = range.split(':')
  if (parts.length !== 2) return null
  const start = parseCellRef(parts[0])
  const end = parseCellRef(parts[1])
  if (!start || !end) return null
  return { sheet, start, end }
}

// ─── Data Resolution ─────────────────────────────────────────────────

/**
 * Resolve chart cell references to actual data for rendering.
 *
 * @param chart - Chart entry from Y.Doc
 * @param getCellValue - Reads a cell value by (row, col). Row is 0-based data row (Excel row 2 = row 0).
 */
export function resolveChartData(
  chart: YDocChartEntry,
  getCellValue: (row: number, col: number) => string | number | null,
): ChartConfig {
  const categoryKey = 'category'
  let categoryLabels: (string | number)[] = []
  let dataLength = 0

  // Get categories from first series that has catRef
  for (const s of chart.series) {
    if (s.catRef) {
      const range = parseRangeRef(s.catRef)
      if (range) {
        categoryLabels = readRange(range.start, range.end, getCellValue)
        dataLength = categoryLabels.length
        break
      }
    }
  }

  // Fallback: infer length from first value series
  if (dataLength === 0) {
    for (const s of chart.series) {
      if (s.valRef) {
        const range = parseRangeRef(s.valRef)
        if (range) {
          const vals = readRange(range.start, range.end, getCellValue)
          dataLength = vals.length
          categoryLabels = vals.map((_, i) => i + 1)
          break
        }
      }
    }
  }

  // Build data array
  const data: Array<Record<string, string | number>> = []
  for (let i = 0; i < dataLength; i++) {
    data.push({ [categoryKey]: categoryLabels[i] ?? i + 1 })
  }

  // Expand data array if any value series is longer than categories
  // (Excel shows all value data points even when categories are shorter)
  const seriesConfigs = chart.series.map((s, idx) => {
    const dataKey = `series_${idx}`
    if (s.valRef) {
      const range = parseRangeRef(s.valRef)
      if (range) {
        const vals = readRange(range.start, range.end, getCellValue)
        // Grow data array if values are longer than categories
        while (data.length < vals.length) {
          data.push({ [categoryKey]: data.length + 1 })
        }
        for (let i = 0; i < vals.length && i < data.length; i++) {
          // Excel treats non-numeric values in numeric series as 0
          const v = vals[i]
          data[i][dataKey] = (typeof v === 'number') ? v : (isNaN(Number(v)) ? 0 : Number(v))
        }
      }
    }
    // Resolve name if it's a cell reference
    let name = s.name
    if (name.includes('!')) {
      const range = parseRangeRef(name)
      if (range) {
        const val = getCellValue(range.start.row, range.start.col)
        if (val != null) name = String(val)
      }
    }
    // Resolve bubble size data
    let bubbleSizeKey: string | undefined
    if (s.bubbleSizeRef) {
      bubbleSizeKey = `bubble_${idx}`
      const range = parseRangeRef(s.bubbleSizeRef)
      if (range) {
        const vals = readRange(range.start, range.end, getCellValue)
        for (let i = 0; i < vals.length && i < data.length; i++) {
          const v = vals[i]
          data[i][bubbleSizeKey] = (typeof v === 'number') ? v : (isNaN(Number(v)) ? 0 : Number(v))
        }
      }
    }
    return {
      name,
      dataKey,
      color: s.color ? `#${s.color}` : undefined,
      seriesChartType: s.seriesChartType,
      yAxisId: s.yAxisId,
      bubbleSizeKey,
    }
  })

  return {
    chartType: chart.chartType,
    title: chart.title,
    data,
    categoryKey,
    series: seriesConfigs,
    stacked: chart.stacked,
    xAxisLabel: chart.xAxisLabel,
    yAxisLabel: chart.yAxisLabel,
    backgroundColor: chart.backgroundColor ? `#${chart.backgroundColor}` : undefined,
    borderColor: chart.borderColor ? `#${chart.borderColor}` : undefined,
    valAxisNumFmt: chart.valAxisNumFmt,
    showDataLabels: chart.showDataLabels,
  }
}

function readRange(
  start: { row: number; col: number },
  end: { row: number; col: number },
  getCellValue: (row: number, col: number) => string | number | null,
): (string | number)[] {
  const values: (string | number)[] = []
  if (start.col === end.col) {
    for (let r = start.row; r <= end.row; r++) values.push(getCellValue(r, start.col) ?? '')
  } else if (start.row === end.row) {
    for (let c = start.col; c <= end.col; c++) values.push(getCellValue(start.row, c) ?? '')
  } else {
    for (let c = start.col; c <= end.col; c++) {
      for (let r = start.row; r <= end.row; r++) values.push(getCellValue(r, c) ?? '')
    }
  }
  return values
}
